import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UserRoles, isAdminRole } from '../../Shared/constants/userRoles';
import { EditUserProps } from '../interfaces/userInterfaces';
import { validateUserFields } from '../utils/fieldValidation';
import { getInputFieldsConfig } from '../utils/userInputFieldConfigs';

export const useEditUser = ({
    userFormData: { firstName, lastName, userRole, category, privilegedMode, orgName },
    setUserFormData,
    editUserMutation,
    userId,
    setIsEditUser,
}: EditUserProps) => {
    const { t } = useTranslation();

    const [fieldErrors, setFieldErrors] = useState<{
        firstName: string;
        lastName: string;
        userRole: string;
        category: string;
        orgName: string;
    }>({
        firstName: '',
        lastName: '',
        userRole: '',
        category: '',
        orgName: '',
    });

    const handleSubmit = () => {
        const errors = validateUserFields(
            firstName,
            lastName,
            t,
            '',
            userRole,
            category,
            orgName
        );
        setFieldErrors(errors);

        if (
            errors.firstName ||
            errors.lastName ||
            errors.userRole ||
            errors.category ||
            errors.orgName
        )
            return;
        const userData = {
            firstName,
            lastName,
            userRole,
            category,
            privilegedMode: isAdminRole(userRole) ? true : privilegedMode,
            orgName,
        };
      editUserMutation.mutate({
        userId,
        userData,
        successMessage: t('user-edited-success-message'),
      });
    };

    const setUserRole = (value: string) =>
        setUserFormData((prev) => {
            const shouldResetCategory = prev.userRole === UserRoles.ACCOUNT_EXECUTIVE && value !== UserRoles.ACCOUNT_EXECUTIVE;
            return {
                ...prev,
                userRole: value,
                category: shouldResetCategory ? null : prev.category,
                orgName: value === UserRoles.ACCOUNT_EXECUTIVE ? prev.orgName : null,
            };
        });
    const setCategory = (value: string) =>
        setUserFormData((prev) => {
            return { ...prev, category: value };
        });
    const setOrgName = (value: string) =>
        setUserFormData((prev) => {
            return { ...prev, orgName: value };
        });
    const setFirstName = (value: string) =>
        setUserFormData((prev) => {
            return { ...prev, firstName: value };
        });
    const setLastName = (value: string) =>
        setUserFormData((prev) => {
            return { ...prev, lastName: value };
        });
    const setPrivilegedMode = (value: boolean) =>
        setUserFormData((prev) => {
            return { ...prev, privilegedMode: value };
        });

    const onCancel = () => setIsEditUser(false);
    const inputFields = getInputFieldsConfig(
        firstName,
        lastName,
        setFirstName,
        setLastName,
        t
    );

    const isUserRoleAccountExecutive = userRole === UserRoles.ACCOUNT_EXECUTIVE;

    return {
        inputFields,
        fieldErrors,
        onCancel,
        handleSubmit,
        setUserRole,
        setCategory,
        setPrivilegedMode,
        setOrgName,
        isUserRoleAccountExecutive,
    };
};
